import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("Authentifizierung wird verarbeitet...");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the hash parameters from URL (Supabase auth uses hash for OAuth)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const providerToken = hashParams.get('provider_token');

        // Also check query params for error messages
        const errorDescription = searchParams.get('error_description');
        const errorCode = searchParams.get('error');

        if (errorCode || errorDescription) {
          setError(errorDescription || 'Ein Fehler ist aufgetreten');
          setTimeout(() => navigate('/auth'), 3000);
          return;
        }

        // If we have tokens from OAuth, set the session
        if (accessToken && refreshToken) {
          setStatus("Session wird erstellt...");
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            throw sessionError;
          }
        }

        // Get the current user
        setStatus("Benutzerdaten werden geladen...");
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError || !user) {
          throw new Error('Benutzer konnte nicht geladen werden');
        }

        // Check if this is OAuth login and update profile with provider data if needed
        const provider = user.app_metadata?.provider;
        if (provider === 'google') {
          setStatus("Google-Profil wird synchronisiert...");
          // Update profile with Google data if names are missing
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', user.id)
            .single();

          if (profile && (!profile.first_name || !profile.last_name)) {
            const googleData = user.user_metadata;
            await supabase
              .from('profiles')
              .update({
                first_name: profile.first_name || googleData?.given_name || googleData?.name?.split(' ')[0],
                last_name: profile.last_name || googleData?.family_name || googleData?.name?.split(' ').slice(1).join(' '),
                avatar_url: profile.avatar_url || googleData?.avatar_url || googleData?.picture,
                name: googleData?.full_name || googleData?.name || `${googleData?.given_name || ''} ${googleData?.family_name || ''}`.trim(),
              })
              .eq('id', user.id);
          }
        }

        // Check if user has a workspace membership
        setStatus("Workspace wird geprüft...");
        const { data: memberships, error: membershipError } = await supabase
          .from('workspace_members')
          .select('workspace_id, personalization_completed_at')
          .eq('user_id', user.id)
          .eq('seat_status', 'active')
          .limit(1);

        if (membershipError) {
          console.error('Error fetching memberships:', membershipError);
        }

        // Determine where to redirect based on the context
        const inviteToken = sessionStorage.getItem('pending_invite_token');
        
        if (inviteToken) {
          // User was accepting an invite, redirect back to invite page
          sessionStorage.removeItem('pending_invite_token');
          navigate(`/invite/${inviteToken}`);
          return;
        }

        // Check if user has workspace and personalization
        if (memberships && memberships.length > 0) {
          const membership = memberships[0];
          
          if (membership.personalization_completed_at) {
            setStatus("Weiterleitung zum Dashboard...");
            navigate('/dashboard');
          } else {
            setStatus("Weiterleitung zum Onboarding...");
            navigate('/onboarding');
          }
        } else {
          setStatus("Weiterleitung zum Onboarding...");
          navigate('/onboarding');
        }

      } catch (err: any) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Ein Fehler ist aufgetreten');
        setTimeout(() => navigate('/auth'), 3000);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="text-destructive text-lg font-medium">
            {error}
          </div>
          <p className="text-muted-foreground">
            Sie werden zur Anmeldeseite weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
        </div>
        <div className="space-y-1">
          <p className="text-foreground font-medium">
            {status}
          </p>
          <p className="text-sm text-muted-foreground">
            Bitte warten Sie einen Moment...
          </p>
        </div>
      </div>
    </div>
  );
}
